import {Box, createStyles} from '@mantine/core';
import React from 'react';

import {ConjugationSite} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteConstants';
import {
  conjugationSiteKey,
  formatConjugationSite,
} from 'molbio/adcs/conjugation-sites/AdcConjugationSiteUtils';
import getTestProps from 'test/lib/cypress/getTestProps';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {CHIP_DISPLAY_LIMIT_TABLE_ROW} from 'ui-library/chips/ChipConstants';
import {BaseChipList} from 'ui-library/chips/ChipList';

const useStyles = createStyles((theme) => ({
  chip: {
    marginTop: theme.spacing.xxs,
    marginBottom: theme.spacing.xxs,
  },
}));

interface ConjugationSiteChipsProps {
  sites: Array<ConjugationSite>;
  onRemove?: (site: ConjugationSite) => void;
  /**
   * Whether the "+N more" chip expands the rest in place. Pass false inside a control that lists the sites
   * itself when clicked, such as a dropdown toggle, which cannot contain the expanded list's button.
   */
  isExpandable?: boolean;
}

/**
 * Chips for the given conjugation sites, showing a few with a "+N more" chip for the rest. Removable when
 * `onRemove` is given.
 */
export function ConjugationSiteChips({
  sites,
  onRemove,
  isExpandable = true,
}: ConjugationSiteChipsProps): JSX.Element | null {
  const {classes} = useStyles();
  const numHiddenSites = sites.length - CHIP_DISPLAY_LIMIT_TABLE_ROW;
  return (
    <BaseChipList
      viewAllOverflowLink={
        isExpandable || numHiddenSites <= 0 ? undefined : (
          <Chip
            key="overflow_chip"
            className={classes.chip}
            size={ChipSize.TABLE_CELL}
            text={`+${numHiddenSites} more`}
          />
        )
      }
      chips={sites.map((site) => (
        <Chip
          key={conjugationSiteKey(site)}
          className={classes.chip}
          size={ChipSize.TABLE_CELL}
          text={formatConjugationSite(site)}
          onRemove={
            onRemove != null
              ? () => {
                  onRemove(site);
                }
              : undefined
          }
        />
      ))}
      maxChips={CHIP_DISPLAY_LIMIT_TABLE_ROW}
      chipSize={ChipSize.TABLE_CELL}
      showMoreSize={ChipSize.TABLE_CELL}
      // Inline, so clicking a chip inside a dropdown toggle still reaches the toggle.
      isVertical={false}
    />
  );
}

interface AdcConjugationSitesDisplayProps {
  sites: Array<ConjugationSite>;
}

/** Read-only display of a Conjugation sites cell. Reads nothing from the row, so it renders outside tables. */
export default function AdcConjugationSitesDisplay({sites}: AdcConjugationSitesDisplayProps): JSX.Element {
  return (
    <Box {...getTestProps({component: 'AdcConjugationSitesDisplay', element: 'root'})}>
      <ConjugationSiteChips sites={sites} />
    </Box>
  );
}
